import FacebookIcon from '@src/svgs/FacebookIcon';
import InstagramOutlineIcon from '@src/svgs/InstagramOutlineIcon';
import LinkedInIcon from '@src/svgs/LinkedInIcon';
import XIcon from '@src/svgs/XIcon';
import React from 'react';

import FacebookPreview, {
  CHANNEL_SLUG as FACEBOOK_SLUG,
} from './FacebookPreview';
import InstagramPreview, {
  CHANNEL_SLUG as INSTAGRAM_SLUG,
} from './InstagramPreview';
import LinkedInPreview, {
  CHANNEL_SLUG as LINKEDIN_SLUG,
} from './LinkedInPreview';
import TwitterPreview, { CHANNEL_SLUG as X_SLUG } from './TwitterPreview';

/**
 * Social previews keyed by distribution channel slug (PLAT-11038).
 *
 * The slugs come from each preview's own `CHANNEL_SLUG` export and match the
 * `slug` field on the DatoCMS Distribution Channel records seeded by PLAT-9753,
 * so a prompt's channel resolves to a preview with no title- or name-based
 * mapping. Adding a preview for a channel that has none yet (youtube, tiktok,
 * threads) is a new entry here and nothing else.
 */
export type SocialPreviewProps = {
  content: string;
  viewport?: 'desktop' | 'mobile';
  authorName?: string;
  avatarUrl?: string;
};

type SocialPreviewEntry = {
  slug: string;
  /** Accessible name for the preview's tab. */
  label: string;
  icon: React.ReactNode;
  Component: React.FC<SocialPreviewProps>;
};

/** Channel opened when a prompt has no channel, or none with a preview. */
export const DEFAULT_PREVIEW_SLUG = LINKEDIN_SLUG;

/** Tab order in the preview pane. */
export const SOCIAL_PREVIEWS: SocialPreviewEntry[] = [
  {
    slug: LINKEDIN_SLUG,
    label: 'LinkedIn preview',
    icon: <LinkedInIcon width="18" height="18" fillColor="currentColor" />,
    Component: LinkedInPreview,
  },
  {
    slug: X_SLUG,
    label: 'X / Twitter preview',
    icon: <XIcon width="16" height="16" fillColor="currentColor" />,
    Component: TwitterPreview,
  },
  {
    slug: FACEBOOK_SLUG,
    label: 'Facebook preview',
    icon: <FacebookIcon width="18" height="18" fillColor="currentColor" />,
    Component: FacebookPreview,
  },
  {
    slug: INSTAGRAM_SLUG,
    label: 'Instagram preview',
    icon: (
      <InstagramOutlineIcon width="18" height="18" strokeColor="currentColor" />
    ),
    Component: InstagramPreview,
  },
];

/**
 * Resolves a channel slug to the slug of the preview to show, falling back to
 * the default when the prompt had no channel (`null`/`undefined`), a custom
 * instruction was used, or the channel has no preview yet (youtube, tiktok,
 * threads).
 */
export function resolvePreviewSlug(channelSlug?: string | null): string {
  if (!channelSlug) return DEFAULT_PREVIEW_SLUG;
  const match = SOCIAL_PREVIEWS.find(p => p.slug === channelSlug);
  return match ? match.slug : DEFAULT_PREVIEW_SLUG;
}
